import { Box, Spinner, Tooltip, useDisclosure, VStack } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import useServicesAdd from 'hooks/useServicesAdd';

import { ExclamationMarkIcon } from 'components/icons/ExclamationMarkIcon';
import { Button, Card, CardBody, Text } from 'components/v4/';
import Image from 'components/v4/Image';
import walkthroughIds from 'pages/services/walkthroughIds';
import ConfirmationModal from './ConfirmationModal';

import path from '@/routes/constants';
import {
    ACS,
    EIGHTBYEIGHT,
    VENDOR_DEFINED,
    OPERATOR_PLATFORM,
} from '../constants';

const ServicesAddCard = ({
    serviceWizard,
    serviceProfile,
    id,
    isButtonDisabled,
    operatorPlatformName,
}) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { darkMode } = useSelector((state) => state.settings);
    const { currentCompany } = useSelector((state) => state.navigationLists);
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [isAdding, setIsAdding] = useState(false);
    const acsPath = `${path.SERVICES_AZURE_CONNECT_SERVICE}/${path.CREATE}`;
    const isServiceProfileSelected =
        serviceWizard?.selectedServiceProfile?.serviceType ===
        serviceProfile?.serviceType;
    const isCurrentServiceAdding =
        serviceWizard?.isCreatingService && isServiceProfileSelected;
    const isCreatingServiceError =
        isServiceProfileSelected &&
        Boolean(serviceWizard.hasCreateServiceError);

    const { ufNewServiceProfileCard } = walkthroughIds.dashboardServices;

    const vendorDefined = serviceProfile?.serviceType === VENDOR_DEFINED;

    const {
        cardBackground,
        cardBorder,
        pointerEvents,
        filterEffect,
        opacity,
        imageSrc,
        serviceName,
        serviceDescription,
        showError,
        setShowError,
    } = useServicesAdd({
        darkMode,
        isAdding,
        isServiceProfileSelected,
        isCreatingServiceError,
        isCurrentServiceAdding,
        serviceProfile,
        serviceWizard,
        vendorDefined,
    });

    const handleCancel = () => {
        serviceWizard.setHasCreateServiceError(null);
        serviceWizard.setIsCreatingService(false);
        setShowError(false);
        setIsAdding(false);
    };

    const onClick = () => {
        switch (serviceProfile.serviceType) {
            case ACS:
                navigate(acsPath);
                break;
            default:
                serviceWizard.createService(serviceProfile);
                break;
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onClick(e);
        setIsAdding(true);
        onClose();
        if (!isCreatingServiceError) {
            queryClient.invalidateQueries({
                queryKey: ['services', `company: ${currentCompany}`],
            });
        }
    };

    const handleOnClick = () => {
        if (
            vendorDefined &&
            serviceProfile?.serviceType !== OPERATOR_PLATFORM
        ) {
            serviceWizard.setSelectedServiceProfile(serviceProfile);
            serviceWizard.setFlow('VendorDefined');
            return;
        }
        if (serviceProfile?.serviceType === EIGHTBYEIGHT) {
            serviceWizard.setSelectedServiceProfile(serviceProfile);
            serviceWizard.setFlow('AddEightByEight');
            return;
        }
        onOpen();
    };

    // Render error state content
    const renderErrorState = () => {
        return (
            <>
                <VStack align="flex-start" spacing={3} width="100%">
                    <Image
                        className="rounded-xl"
                        src={imageSrc}
                        boxSize="50px"
                        objectFit="contain"
                    />
                    <Text fontWeight="bold">{serviceName}</Text>
                    <Text color="red.700" fontSize="sm">
                        <ExclamationMarkIcon />{' '}
                        {serviceWizard?.hasCreateServiceError}
                    </Text>
                </VStack>
                <Button
                    sx={{ width: '100%' }}
                    colorScheme="red"
                    variant="outline"
                    onClick={handleCancel}>
                    Cancel
                </Button>
            </>
        );
    };

    // Render normal state content
    const renderNormalState = () => {
        const hasTooltip = serviceDescription.long;

        return (
            <>
                <VStack align="flex-start" spacing={3} width="100%">
                    <Image
                        className="rounded-xl"
                        src={imageSrc}
                        boxSize="50px"
                        objectFit="contain"
                    />
                    <Text fontWeight="bold">{serviceName}</Text>
                    <Text>{serviceDescription.short}</Text>
                </VStack>
                <Box width="100%">
                    <Tooltip
                        hasArrow
                        label={
                            hasTooltip
                                ? serviceDescription.long
                                : `${operatorPlatformName} must be added before ${serviceName} can be added`
                        }
                        isDisabled={!hasTooltip && !isButtonDisabled}>
                        <Box width="100%">
                            <Button
                                sx={{ width: '100%' }}
                                onClick={handleOnClick}
                                isDisabled={isButtonDisabled}>
                                + Add
                            </Button>
                        </Box>
                    </Tooltip>
                </Box>
            </>
        );
    };

    return (
        <>
            <Card
                bg={cardBackground}
                border={cardBorder}
                borderRadius={10}
                pointerEvents={pointerEvents}
                key={id}
                data-walkthroughid={`${ufNewServiceProfileCard}/${id}`}
                sx={{
                    filter: filterEffect,
                    opacity: opacity,
                    transition: 'all 0.3s ease',
                }}>
                <CardBody position="relative" height="100%">
                    {isServiceProfileSelected &&
                        isAdding &&
                        (!showError ||
                            !serviceWizard.hasCreateServiceError) && (
                            <Box
                                pointerEvents="none"
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '100%',
                                    position: 'absolute',
                                    width: '100%',
                                    left: '0',
                                    top: '0',
                                    zIndex: 1,
                                }}>
                                <Spinner
                                    thickness="4px"
                                    emptyColor="gray.200"
                                    color="brand.500"
                                    size="xl"
                                />
                            </Box>
                        )}
                    <VStack
                        align="flex-start"
                        spacing={3}
                        width="100%"
                        height="100%"
                        justifyContent="space-between">
                        {showError ? renderErrorState() : renderNormalState()}
                    </VStack>
                </CardBody>
            </Card>
            <ConfirmationModal
                serviceProfile={serviceProfile}
                isOpen={isOpen}
                onOpen={onOpen}
                onClose={onClose}
                vendorDefined={vendorDefined}
                handleSubmit={handleSubmit}
            />
        </>
    );
};

export default ServicesAddCard;
